/**
 * useDocumentGap (P278.3) -- the standing-start gap report: per framework,
 * which expected deliverables are present / candidate / missing, driven by
 * the P278.2 document->deliverable mappings.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type {
  CandidateDocuments,
  ControlDocumentCandidate,
  DocumentGapReport,
} from "shared/api/types";

/**
 * Round 14 A2 -- the control review panel's "N policy documents cover
 * this control" is a separate query, keyed ["control", id,
 * "covering-documents"], that no mapping mutation refreshed. Round 14
 * read "1" beside a Test of Design that had just read three. Any change
 * to what covers a control has to reach it.
 */
export function invalidateCoveringDocuments(qc: QueryClient): void {
  void qc.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === "control" && q.queryKey[2] === "covering-documents",
  });
}

export const documentGapKeys = {
  report: (framework: string | null) => ["document-gap", framework ?? "all"] as const,
  candidates: (deliverableId: string) =>
    ["deliverable-candidate-documents", deliverableId] as const,
};

export function useDocumentGap(
  framework: string | null,
): UseQueryResult<DocumentGapReport, ApiError> {
  return useQuery({
    queryKey: documentGapKeys.report(framework),
    queryFn: () =>
      api.get<DocumentGapReport>(
        framework
          ? `/v1/framework/document-gap?framework=${encodeURIComponent(framework)}`
          : "/v1/framework/document-gap",
      ),
  });
}

/** P286.3: lazily rank held documents for an uncovered deliverable. */
export function useCandidateDocuments(
  deliverableId: string,
  enabled: boolean,
): UseQueryResult<CandidateDocuments, ApiError> {
  return useQuery({
    queryKey: documentGapKeys.candidates(deliverableId),
    queryFn: () =>
      api.get<CandidateDocuments>(
        `/v1/framework/deliverables/${deliverableId}/candidate-documents`,
      ),
    enabled: enabled && Boolean(deliverableId),
  });
}

/**
 * B1 -- confirm a candidate document as covering a deliverable.
 *
 * The gap view listed candidates with a similarity percentage and no
 * verb, so a preparer who could SEE the right document had no way to
 * say so. The endpoint has admitted a preparer since P278; only the
 * button was missing, which is why Test of Design has been a dead end
 * for three rounds.
 */
export function useConfirmCandidate(): UseMutationResult<
  unknown,
  ApiError,
  { documentId: string; deliverableId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, deliverableId }) =>
      // The mapping router is mounted under /v1/admin/framework, not
      // /v1/framework. Both tests mocked this hook, so neither could
      // catch the wrong URL -- it took a live drive, which returned a
      // flat 404 on the first real click.
      api.post(`/v1/admin/framework/documents/${documentId}/mappings`, {
        expected_deliverable_id: deliverableId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["document-gap"] });
      void qc.invalidateQueries({ queryKey: ["deliverable-candidate-documents"] });
      invalidateCoveringDocuments(qc);
    },
  });
}

/**
 * Round 13 A4 -- undo "This covers it".
 *
 * The endpoint has existed since P278 and nothing in the interface called
 * it, so a document uploaded in error was permanently part of the
 * evidence for that control and read again by the next design test. A
 * confirmed-mapped document is also refused deletion by design, which
 * made this the ONLY containment step available to a bank that discovers
 * a bad document after the fact.
 */
export function useRejectMapping(): UseMutationResult<
  unknown,
  ApiError,
  { linkId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId }) =>
      api.post(`/v1/admin/framework/mappings/${linkId}/reject`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["document-gap"] });
      void qc.invalidateQueries({ queryKey: ["deliverable-candidate-documents"] });
      invalidateCoveringDocuments(qc);
    },
  });
}

/**
 * B1 -- documents that could cover a control, offered where the
 * preparer hit the wall rather than on another screen.
 */
/**
 * B3 -- record that a document covers a control the catalogue never
 * named a deliverable for. Six rounds of the preparer meeting an empty
 * picker: the gap is in the catalogue, not in her work.
 */
export function useMapDocumentToControl(): UseMutationResult<
  unknown,
  ApiError,
  { controlId: string; documentId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ controlId, documentId }) =>
      api.post(`/v1/framework/controls/${controlId}/map-document`, {
        document_id: documentId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["control-candidate-documents"] });
      invalidateCoveringDocuments(qc);
      void qc.invalidateQueries({ queryKey: ["document-gap"] });
    },
  });
}

export function useControlCandidateDocuments(
  controlId: string,
  /** Round 17 A30 -- a typed filter searches the whole locker. */
  q = "",
): UseQueryResult<ControlDocumentCandidate[], ApiError> {
  const needle = q.trim();
  return useQuery({
    queryKey: ["control-candidate-documents", controlId, needle],
    queryFn: () =>
      api.get<ControlDocumentCandidate[]>(
        `/v1/framework/controls/${controlId}/candidate-documents` +
          (needle ? `?q=${encodeURIComponent(needle)}` : ""),
      ),
  });
}

/** Round 17 A30 -- per deliverable, the most the server returns; a list
 *  that reaches it says the rest are on the gap page. */
export const CANDIDATE_LIMIT = 25;

/**
 * Round 14 A3 -- take a document out of a control's evidence, from the
 * control. It covers the control through one mapping per deliverable and
 * the server rejects every one; the design test that read it then says
 * the document behind it was withdrawn.
 */
export function useWithdrawCoveringDocument(
  controlId: string,
): UseMutationResult<unknown, ApiError, { documentId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId }) =>
      api.post(
        `/v1/framework/controls/${controlId}/covering-documents/${documentId}/withdraw`,
        {},
      ),
    onSuccess: () => {
      invalidateCoveringDocuments(qc);
      void qc.invalidateQueries({ queryKey: ["control-tests"] });
      void qc.invalidateQueries({ queryKey: ["document-gap"] });
      void qc.invalidateQueries({ queryKey: ["control-candidate-documents"] });
    },
  });
}
